/**
 * 계약 인원 초과 — 묻고, 근거를 받는다
 * ---------------------------------------------------------------------------
 * ## 왜 묻는가
 * 계약 10명짜리 기업에 11번째 구성원을 넣으면 요금이 늘어난다. 예전에는
 * 아무 말 없이 들어갔고, 담당자는 **다음 달 청구서를 받고서야** 알았다.
 *
 * 그래서 서버가 거절하면서 **얼마가 늘어나는지**를 함께 돌려준다. 이 파일은
 * 그 금액을 띄우고 「예/아니오」를 물은 뒤, 「예」면 사유를 받아 돌려준다.
 *
 * ## 사유를 반드시 받는 이유
 * 몇 달 뒤 "청구서가 왜 늘었냐" 는 질문이 온다. 그때 "누가 언제 무엇 때문에
 * 늘렸다" 를 댈 수 없으면 설명할 방법이 없다. 서버가 그 기록을 남기므로
 * 여기서는 빈 사유를 보내지 않는 것만 지킨다.
 *
 * ## 제품이 늘어나도 같다
 * 구성원은 Hub 에서만 만들어진다. CRM·ECO 도 Hub 를 불러 만들기 때문에
 * 같은 응답을 받는다 — 제품마다 이 창을 다시 만들 필요가 없다.
 */

import { Modal, Input, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import type { NormalizedError } from '../../lib/api-client';

const { Text, Paragraph } = Typography;

/** 서버가 돌려준 판정. seat.service.ts 의 SeatDecision 과 같은 모양이다. */
export interface SeatDecision {
  activeUsers: number;
  requiredUnits: number;
  allowed: boolean;
  approvable: boolean;
  monthlyDelta: number;
  affected: Array<{
    productName: string;
    planName: string;
    unitLabel: string;
    contractedUnits: number;
    toUnits: number;
    delta: number;
  }>;
  blocked: Array<{
    productName: string;
    planName: string;
    unitLabel: string;
    maxUnits: number | null;
    requiredUnits: number;
  }>;
  message: string | null;
}

const won = (n: number) => Math.round(n).toLocaleString('ko-KR');

function seatOf(err: NormalizedError): SeatDecision | null {
  const seat = err.payload?.seat;
  return seat && typeof seat === 'object' ? (seat as SeatDecision) : null;
}

/** 승인하면 넘어갈 수 있는 거절인가 */
export function isSeatApprovalRequired(err: NormalizedError): boolean {
  return err.code === 'SEAT_APPROVAL_REQUIRED' && seatOf(err) !== null;
}

/** 승인해도 안 되는 거절인가 (요금제 한도) */
export function isSeatLimitExceeded(err: NormalizedError): boolean {
  return err.code === 'SEAT_LIMIT_EXCEEDED';
}

/**
 * 요금제 한도에 걸렸다. 승인으로 해결되지 않으므로 묻지 않고 알리기만 한다.
 */
export function showSeatLimit(err: NormalizedError): void {
  const seat = seatOf(err);
  Modal.error({
    title: '요금제의 최대 인원을 넘습니다',
    width: 560,
    content: (
      <div style={{ fontSize: 13, lineHeight: 1.8 }}>
        <Paragraph style={{ marginBottom: 8 }}>{err.message}</Paragraph>
        {seat?.blocked.map((b) => (
          <div key={`${b.productName}-${b.planName}`}>
            · {b.productName} · {b.planName} — 최대 {b.maxUnits}
            {b.unitLabel}, 필요 {b.requiredUnits}
            {b.unitLabel}
          </div>
        ))}
        <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
          상위 요금제로 변경한 뒤 다시 등록해 주세요. 요금제 변경은 운영자에게 문의하시면
          됩니다.
        </Paragraph>
      </div>
    ),
    okText: '확인',
  });
}

/**
 * 「계속할까요」를 묻는다.
 *
 * 「예」를 누르면 입력한 사유를, 「아니오」나 창을 닫으면 `null` 을 돌려준다.
 * 사유가 비어 있으면 「예」가 닫히지 않는다 — 근거 없는 승인은 기록해도
 * 쓸모가 없다.
 */
export function askSeatApproval(
  err: NormalizedError,
  /** 늘어나는 인원. 역할을 여러 명에게 줄 때는 1 이 아니다. */
  joining = 1,
): Promise<string | null> {
  const seat = seatOf(err);
  if (!seat) return Promise.resolve(null);

  let reason = '';

  return new Promise((resolve) => {
    const modal = Modal.confirm({
      title: '계약 인원을 초과합니다',
      icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
      width: 620,
      okText: '예, 추가하고 요금을 올립니다',
      cancelText: '아니오',
      okButtonProps: { danger: true },
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <Paragraph style={{ marginBottom: 10 }}>
            지금 <b>{seat.activeUsers}명</b>이고,{' '}
            {joining > 1 ? `${joining}명을 더 넣으면 ` : '한 명을 더 등록하면 '}
            <b>{seat.requiredUnits}명</b>이 됩니다.
          </Paragraph>

          <div
            style={{
              border: '1px solid #ffe58f',
              background: '#fffbe6',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 12,
            }}
          >
            {seat.affected.map((a) => (
              <div key={`${a.productName}-${a.planName}`}>
                · {a.productName} · {a.planName} — 계약 {a.contractedUnits}
                {a.unitLabel} → <b>{a.toUnits}</b>
                {a.unitLabel}, 월 <b>{won(a.delta)}원</b> 추가
              </div>
            ))}
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ffe58f' }}>
              매달 <b style={{ fontSize: 15 }}>{won(seat.monthlyDelta)}원</b>이 더 청구됩니다{' '}
              <Text type="secondary">(부가세 별도)</Text>
            </div>
          </div>

          <Paragraph style={{ marginBottom: 6 }}>
            <b>사유를 적어 주세요.</b> 기업 정보에 기록되어, 나중에 청구 금액이 왜 늘었는지
            설명하는 근거가 됩니다.
          </Paragraph>
          <Input.TextArea
            rows={2}
            maxLength={500}
            placeholder="예: 영업팀 신규 입사자 충원 (대표 승인 완료)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      onOk: () => {
        const trimmed = reason.trim();
        if (!trimmed) {
          // 창을 닫지 않는다. 사유 없이 넘어가면 기록이 비어 쓸모가 없다.
          modal.update({ okButtonProps: { danger: true } });
          return Promise.reject(new Error('사유를 입력하세요'));
        }
        resolve(trimmed);
        return Promise.resolve();
      },
      onCancel: () => resolve(null),
    });
  });
}
